<template>
  <div class="venue-products">
    <ez-product-list
      :headers="{
        name: () => 'Product',
        price: () => 'Price per unit',
        distributor: () => `${$t('global.distributor').toLowerCase()}`,
      }"
      :columns="[
        'name',
        ...(isB2BPlatform || isFreemium || !isLoggedIn ? [] : ['distributor']),
        ...(isB2BPlatform || isFreemium || !isLoggedIn ? ['tags'] : []),
        'unit',
        ...(isLoggedIn || (!isLoggedIn && token) ? ['price'] : []),
        ...(meta.showAvailability ? ['availability'] : []),
      ]"
      :columnProps="{
        name: { class: 'medium-cell' },
        unit: { class: 'medium-cell unit-cell' },
        price: { class: 'price-cell price-cell-overflow' },
        availability: { class: 'text-right-cell medium-cell' },
        distributor: { class: 'distributor-cell' },
      }"
      :products="productsData"
      :previouslyOrderedProducts="previouslyOrderedProductsData"
      :previously-ordered-products-count="previouslyOrderedProductsCount"
      :hasQuickAdd="$permission.has('createOrder')"
      :responsiveInfo="false"
      :b2bSupplier="b2bSupplier"
      @rowClick="openDrawerAndLoadProduct"
      @filterPreviouslyOrdered="fetchPreviouslyOrdered"
      :token="token"
      :isListView="isListView"
    >
      <template #filters>
        <div class="filters-row">
          <ez-filter-list
            :filters="listFilters"
            @filterUpdated="updateFilters"
            @resetFilter="resetFilters(FilterArea.List)"
            class="venue-products__filters"
          >
            <ez-input
              formKey="filters"
              label="search"
              name="term"
              class="search"
              placeholder="Search for a Product"
              :data-cy="outletCy.PRODUCTS.GENERAL.INPUT__SEARCH"
            >
              <template #suffix>
                <font-awesome-icon icon="search" />
              </template>
            </ez-input>
            <v-select-search
              v-if="isLoggedIn && !(isB2BPlatform || isFreemium)"
              class="ml-16 hide-on-mobile"
              formKey="filters"
              name="distributorId"
              :search="distributorFilterUrl"
              :selected="dropdownFilters.distributorId"
              :request-params="{ connected: true, limit: undefined }"
              :data="distributors"
              :placeholder="`All ${$t('global.distributors')}`"
              :searchPlaceholder="`Select ${$t('global.distributor')}`"
              align-left
              :data-cy="outletCy.PRODUCTS.GENERAL.BUTTON__SUPPLIER_FILTER"
              :min-length="3"
            >
              <template #result="{ result }">
                <v-distributor-entity-info :distributor="result" />
              </template>
            </v-select-search>
            <ez-product-group-filter
              v-if="!(isB2BPlatform || isFreemium)"
              class="ml-16 hide-on-mobile"
              name="productGroupId"
              label="Product Group"
              :productGroups="productGroupsFilter"
              :selected="dropdownFilters.productGroupId"
            />
          </ez-filter-list>
          <v-filter-dropdown
            :filters="dropdownFilters"
            @filterUpdated="updateFilters"
            @resetFilters="resetFilters(FilterArea.Dropdown)"
            ref="filtersGroup"
            class="ml-16"
          >
            <template #firstRow>
              <v-select-search
                v-if="isLoggedIn && !(isB2BPlatform || isFreemium)"
                formKey="filters"
                name="distributorId"
                :label="`${$t('global.distributor')}`"
                :search="distributorFilterUrl"
                :selected="dropdownFilters.distributorId"
                :request-params="{ connected: true, limit: undefined }"
                :data="distributors"
                :placeholder="`All ${$t('global.distributors')}`"
                :searchPlaceholder="`Select ${$t('global.distributor')}`"
                align-left
                :data-cy="outletCy.PRODUCTS.GENERAL.BUTTON__SUPPLIER_FILTER"
                :min-length="3"
                is-full-width
              >
                <template #result="{ result }">
                  <v-distributor-entity-info :distributor="result" />
                </template>
              </v-select-search>
              <ez-product-group-filter
                name="productGroupId"
                label="Product Group"
                :productGroups="productGroupsFilter"
                :selected="dropdownFilters.productGroupId"
                isFullWidth
              />
              <v-select-search
                class="input-group"
                formKey="filters"
                name="categoryId"
                label="Category"
                :selected="dropdownFilters.categoryId"
                :data="categoryOptions"
                :placeholder="`All Categories`"
                :searchPlaceholder="`Select Category`"
                align-left
                isFullWidth
                :data-cy="outletCy.PRODUCTS.GENERAL.BUTTON__CATEGORY_FILTER"
              >
                <template #result="{ result }">
                  <v-category-entity-info :category="result" :is-child="result.isChild" />
                </template>
              </v-select-search>
            </template>
            <template #secondRow>
              <ez-checkbox
                class="status-checkbox mt-12 mb-12"
                :checked="dropdownFilters.previouslyOrdered"
                key="previouslyOrdered"
                formKey="filters"
                name="previouslyOrdered"
                label="Previously Ordered"
                @change="togglePreviouslyOrderedFilter"
              />
            </template>
            <template #thirdRow v-if="isB2BPlatform || isFreemium || !isLoggedIn">
              <ez-tags-filter
                class="tags-filter"
                name="productTags"
                ref="tagsFilter"
                :value="tagFilter"
                :supportsMatchType="true"
              />
            </template>
          </v-filter-dropdown>
          <div class="view-switch">
            <ez-button type="link" :class="{ selected: !isListView }" @click="setView('grid')">
              <font-awesome-icon icon="th-large" />
            </ez-button>
            <ez-button type="link" :class="{ selected: isListView }" @click="setView('list')">
              <font-awesome-icon icon="list-ul" />
            </ez-button>
          </div>
        </div>
      </template>
      <template #alert>
        <ez-alert
          type="yellow"
          v-if="(b2bSupplier?.onboardingToken || $route.query.token) && !isLoggedIn"
        >
          <template #icon><font-awesome-icon icon="exclamation-circle" /></template>
          <div>Open an account to place an order.</div>
          <template #cta>
            <ez-button type="yellow-link" @click="goToTheRegisterFlow"> Open an account </ez-button>
          </template>
        </ez-alert>
      </template>
      <template v-if="!productsData.length && !isLoading">
        <empty-state v-if="hasFilters">
          <template #badge>
            <img src="@/assets/no-products-search-state.svg" alt="" />
          </template>
          <template #title>No products match this search</template>
          <template #info>Try with a different search.</template>
        </empty-state>
        <empty-state v-else>
          <template #badge>
            <img src="@/assets/no-product-empty-state.svg" alt="" />
          </template>
          <template #title>No products listed</template>
          <template #info>
            As soon as you get connected to a supplier, you'll see a list of available products
            here.
          </template>
        </empty-state>
      </template>
      <ez-loader v-else :show="isLoading">Loading...</ez-loader>
      <ez-load-more v-if="meta.nextId && !isLoadingMore" @loadMore="onLoadMoreClick" />
    </ez-product-list>
    <product-drawer
      ref="productDrawer"
      :productId="productId"
      @close="resetDrawerRoute"
      :product="selectedProduct"
      :hide-price="!(isLoggedIn || !isB2BPlatform || (isB2BPlatform && token))"
      :token="token"
      :b2b-supplier="b2bSupplier"
    />
    <div v-if="isLoadingMore" class="u-text-center mt-12">
      <ez-spinner />
    </div>
    <upload-invoice ref="uploadInvoice" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { outlet as outletCy } from '@orderez/qa-utils/dist/orderEz/outlet';
import EzFilterList from '@/components/ui/FilterList';
import EzInput from '@/components/ui/Input';
import EzLoadMore from '@/components/ui/LoadMore';
import ProductDrawer from '@/views/platform/venue/ProductDrawer.vue';
import EzProductList from '@/views/common/products/List.vue';
import EmptyState from '@/views/common/empty-state';
import VSelectSearch from '@/components/v3/patterns/VSelectSearch';
import VDistributorEntityInfo from '@/components/v3/patterns/VDistributorEntityInfo';
import VCategoryEntityInfo from '@/components/v3/patterns/VCategoryEntityInfo';
import { debounce, getContextId } from '@/util/utils';
import EzSpinner from '@/components/ui/Spinner';
import EzLoader from '@/components/ui/Loader';
import UploadInvoice from '@/views/platform/venue/orders/actions/UploadInvoice.vue';
import { LOADING_KEY, TagType } from '@/util/constants';
import VFilterDropdown from '@/components/v3/patterns/VFilterDropdown';
import dayjs from 'dayjs';
import EzProductGroupFilter from '@/components/ui/Filter/ProductGroup.vue';
import EzTagsFilter, { defaultTagFilter } from '@/components/ui/Filter/Tags.vue';
import EzAlert from '@/components/ui/Alert/EzAlert.vue';
import EzButton from '@/components/ui/Button/EzButton.vue';
import EzCheckbox from '@/components/ui/Checkbox/Checkbox';

const FilterArea = {
  List: 'list',
  Dropdown: 'dropdown',
};

export default {
  components: {
    VSelectSearch,
    EzFilterList,
    EzInput,
    EzLoadMore,
    ProductDrawer,
    EzProductList,
    EmptyState,
    EzSpinner,
    VDistributorEntityInfo,
    VCategoryEntityInfo,
    EzLoader,
    UploadInvoice,
    VFilterDropdown,
    EzProductGroupFilter,
    EzTagsFilter,
    EzAlert,
    EzButton,
    EzCheckbox,
  },
  props: {
    productId: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      dropdownFilters: { previouslyOrdered: false },
      listFilters: { term: null },
      quantity: 1,
      totalPrice: 0,
      outletCy,
      distributors: [],
      productsData: [],
      previouslyOrderedProductsData: [],
      previouslyOrderedProductsCount: null,
      meta: {},
      selectedProduct: {},
      categories: [],
      FilterArea,
      productGroups: [],
      token: null,
      tagFilter: defaultTagFilter(),
      b2bSupplier: {},
      isListView: true,
    };
  },
  computed: {
    ...mapGetters('cart', ['getProductCount']),
    ...mapGetters('loading', ['getLoading', 'isSomeLoading']),
    ...mapState('entities/users', ['loggedUser', 'context']),
    isLoadingMore() {
      return this.getLoading(LOADING_KEY.LOAD_MORE_PRODUCTS);
    },
    isLoading() {
      return this.isSomeLoading([
        LOADING_KEY.FETCH_PRODUCTS,
        LOADING_KEY.VENUE_FETCH_DISTRIBUTORS_FILTER,
        LOADING_KEY.VENUE_FETCH_CATEGORIES_FILTER,
        LOADING_KEY.FETCH_DISTRIBUTOR_B2B,
      ]);
    },
    filters() {
      return { ...this.listFilters, ...this.dropdownFilters };
    },
    productGroupsFilter() {
      return [
        { id: null, name: 'All Product Groups' },
        { id: '', name: 'Unassigned' },
        ...this.productGroups,
      ];
    },
    categoryOptions() {
      const compareByName = (a, b) => a.name.localeCompare(b.name);
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      const categories = this.categories.sort(compareByName);

      let options = [];
      let parent = {};
      let children = [];
      categories.sort(compareByName).forEach(cat => {
        parent = {
          id: cat.id,
          name: cat.name,
          isChild: false,
        };
        children = (cat.children || [])
          .map(catChild => ({
            id: catChild.id,
            name: catChild.name,
            isChild: true,
          }))
          .sort(compareByName);

        options = [...options, parent, ...children];
      });

      return options;
    },
    hasFilters() {
      return !!this.filters.term || !!this.filters.categoryId || !!this.filters.distributorId;
    },
    venueId() {
      return getContextId();
    },
    distributorFilterUrl() {
      return `/venue/filters/distributors?for=products`;
    },
    isB2BPlatform() {
      return ![
        'localhost',
        'app-dev.orderez.co',
        'app-dev.foodrazor.co',
        'app-staging.orderez.co',
        'app-staging.foodrazor.co',
        'app.orderez.co',
        'app.foodrazor.com',
      ].includes(window.location.hostname);
    },
    isLoggedIn() {
      return Object.keys(this.loggedUser).length > 0;
    },
    isFreemium() {
      return this.$permission.isFree;
    },
  },
  methods: {
    resetDrawerRoute() {
      this.$router.replace({
        name: 'venue-orders-products',
        ...(this.token ? { query: { token: this.token } } : {}),
      });
    },
    setView(view) {
      this.isListView = view === 'list';
      localStorage.setItem('viewPreference', view);
    },
    filterClosed() {
      const { tagsFilter } = this.$refs;
      if (tagsFilter) tagsFilter.revert();
    },
    goToTheRegisterFlow() {
      if (this.$route.query.token)
        window.open(`/onboarding?token=${this.$route.query.token}`, '_blank');
      else window.open(`/onboarding?token=${this.b2bSupplier.onboardingToken}`, '_blank');
    },
    resetData() {
      this.productsData = [];
      this.previouslyOrderedProductsData = [];
      this.previouslyOrderedProductsCount = 0;
      this.meta = {};
    },
    async resetFilters(area) {
      if (area === FilterArea.List) {
        Object.keys(this.listFilters).forEach(key => {
          this.listFilters[key] = null;
          if (key !== 'term') this.dropdownFilters[key] = null;
        });
      } else if (area === FilterArea.Dropdown) {
        this.dropdownFilters = {};
        delete this.listFilters.distributorId;
        delete this.listFilters.productGroupId;
        this.tagFilter = defaultTagFilter();
      }
      this.$nextTick(() => this.$refs.filtersGroup.closeFilterDropdown());
      this.$emit('updateFilter', this.filters);
      this.resetData();
      if (this.isB2BPlatform || this.isFreemium) await this.fetchPreviouslyOrderedProducts();
      return this.refresh({
        filters: this.filters,
        ...(this.isB2BPlatform || this.isFreemium ? {} : { sortBy: 'name' }),
      });
    },
    async openDrawerAndLoadProduct({ id }) {
      try {
        const {
          data: { data },
        } = !this.isLoggedIn
          ? await this.publicVenueFetchProduct({
              id,
              ...(this.token ? { token: this.token } : {}),
            })
          : await this.venueFetchProduct({
              venueId: this.venueId,
              id,
            });

        this.selectedProduct = data;
        await this.$router.push({
          name: 'venue-orders-products',
          params: { id },
          ...(this.token ? { query: { token: this.token } } : {}),
        });
        this.fetchTotalPrice(id);
      } catch (e) {
        this.resetDrawerRoute();
        console.error(e);
      }
    },
    async loadDrawerProduct({ id }) {
      try {
        const {
          data: { data },
        } = !this.isLoggedIn
          ? await this.publicVenueFetchProduct({
              id,
              ...(this.token ? { token: this.token } : {}),
            })
          : await this.venueFetchProduct({
              venueId: this.venueId,
              id,
            });

        this.selectedProduct = data;
        this.fetchTotalPrice(id);
      } catch (e) {
        this.resetDrawerRoute();
        console.error(e);
      }
    },
    fetchTotalPrice(id) {
      if (!this.isLoggedIn) {
        this.fetchPublicTotalProductPrice({
          id,
          quantity: this.quantity,
          ...(this.token ? { token: this.token } : {}),
        }).then(({ data }) => {
          this.totalPrice = data.data.price;
          this.$refs.productDrawer.open();
        });
      } else {
        this.fetchTotalProductPrice({
          id,
          quantity: this.quantity,
        }).then(({ data }) => {
          this.totalPrice = data.data.price;
          this.$refs.productDrawer.open();
        });
      }
    },
    async fetchProductGroups() {
      const {
        data: { data },
      } = !this.isLoggedIn
        ? await this.publicVenueFetchProductGroups({
            ...(this.token ? { token: this.token } : {}),
          })
        : await this.venueFetchProductGroups({
            scope: this.isFreemium ? 'distributor' : 'venue',
          });
      this.productGroups = data;
    },
    async fetchProductTags() {
      if (!this.isLoggedIn) {
        await this.publicFetchTags({
          query: { type: TagType.Product },
            ...(this.token ? { token: this.token } : {})
        });
      } else {
        await this.venueFetchTags({ query: { type: TagType.Product } });
      }
    },
    async fetchB2BSupplier() {
      const { data } = await this.fetchB2BLoginContent({
        ...(this.token ? { token: this.token } : {}),
      });
      this.b2bSupplier = data.data;
      const viewPreference = localStorage.getItem('viewPreference') || null;
      if (!viewPreference) {
        this.isListView = data.data.customerView !== 'grid';
        localStorage.setItem('viewPreference', data.data.customerView);
      } else {
        this.isListView = viewPreference !== 'grid';
      }
    },
    updateFilters: debounce(function deb(filterName, event) {
      if (filterName === 'term') {
        this.listFilters = {
          ...this.listFilters,
          term: event,
        };
      } else if (filterName === 'distributorId') {
        this.dropdownFilters = {
          ...this.dropdownFilters,
          distributorId: event.id,
        };
        this.listFilters = {
          ...this.listFilters,
          distributorId: event.id,
        };
      } else if (filterName === 'productGroupId') {
        this.dropdownFilters = {
          ...this.dropdownFilters,
          productGroupId: event.id,
        };
        this.listFilters = {
          ...this.listFilters,
          productGroupId: event.id,
        };
      } else {
        const { tagsFilter } = this.$refs;
        if (tagsFilter) this.tagFilter = tagsFilter.getValue();
        const tagIds = this.tagFilter.tags.map(item => item.id);
        this.dropdownFilters = {
          ...this.dropdownFilters,
          ...(typeof filterName.categoryId === 'object' &&
            filterName.categoryId !== null && { categoryId: filterName.categoryId.id }),
          ...(typeof filterName.distributorId === 'object' &&
            filterName.distributorId !== null && { distributorId: filterName.distributorId.id }),
          ...(typeof filterName.productGroupId === 'object' &&
            filterName.productGroupId !== null && { productGroupId: filterName.productGroupId.id }),
          ...(tagIds.length
            ? { tagIds, tagMode: this.tagFilter.matchType }
            : { tagIds: null, tagMode: null }),
        };
        this.listFilters = {
          ...this.listFilters,
          ...(typeof filterName.distributorId === 'object' &&
            filterName.distributorId !== null && { distributorId: filterName.distributorId.id }),
          ...(typeof filterName.productGroupId === 'object' &&
            filterName.productGroupId !== null && { productGroupId: filterName.productGroupId.id }),
        };

        if (filterName.distributorId === undefined) {
          delete this.dropdownFilters.distributorId;
          delete this.listFilters.distributorId;
        }
      }
      this.$nextTick(() => this.$refs.filtersGroup.closeFilterDropdown());

      this.resetData();
      this.refresh({
        filters: this.filters,
        ...(this.isB2BPlatform || this.isFreemium ? {} : { sortBy: 'name' }),
      });
      if (!Object.values(this.filters).filter(x => x === '' || x).length)
        this.fetchPreviouslyOrderedProducts();

      this.$emit('updateFilter', this.filters);
    }, 300),
    togglePreviouslyOrderedFilter(ev) {
      this.dropdownFilters.previouslyOrdered = ev;
    },
    onLoadMoreClick() {
      this.refresh({
        filters: this.filters,
        loadingKey: LOADING_KEY.LOAD_MORE_PRODUCTS,
        ...(this.isB2BPlatform || this.isFreemium ? {} : { sortBy: 'name' }),
      });
    },
    async fetchSuppliersFilter() {
      const {
        data: { data: distributors },
      } = await this.venueFetchFilterSuppliers({
        venueId: this.venueId,
        query: {
          connected: true,
        },
      });
      this.distributors = distributors;
    },
    async fetchCategoriesFilter() {
      const {
        data: { data },
      } = !this.isLoggedIn
        ? await this.publicVenueFetchCategoriesNew({
            ...(this.token ? { token: this.token } : {}),
          })
        : await this.venueFetchCategoriesNew();
      this.categories = data;
    },
    async refresh(query = {}) {
      if (!this.isLoggedIn) {
        await this.refreshPublic(query);
        return;
      }
      const combinedQuery = {
        ...query,
        ...this.meta,
      };
      try {
        const {
          data: { data, meta },
        } = await this.venueFetchProducts({
          venueId: this.venueId,
          ...(this.isB2BPlatform || this.isFreemium ? {} : { sortBy: 'name' }),
          ...combinedQuery,
        });

        if (query.loadingKey !== LOADING_KEY.LOAD_MORE_PRODUCTS) {
          this.productsData = data;
        } else {
          this.productsData = [...this.productsData, ...data];
        }
        this.meta = meta;
        this.UPDATE_META({ meta });
      } catch (e) {
        console.error(e);
      }
    },
    async refreshPublic(query = {}) {
      const combinedQuery = {
        ...query,
        ...this.meta,
      };

      try {
        const {
          data: { data, meta },
        } = await this.publicVenueFetchProducts({
          ...combinedQuery,
          ...(this.token ? { token: this.token } : {}),
        });

        if (query.loadingKey !== LOADING_KEY.LOAD_MORE_PRODUCTS) {
          this.productsData = data;
        } else {
          this.productsData = [...this.productsData, ...data];
        }
        this.meta = meta;
        this.UPDATE_META({ meta });
      } catch (e) {
        console.error(e);
      }
    },
    async fetchPreviouslyOrderedProductsPublic() {
      try {
        const {
          data: { data, meta },
        } = await this.publicVenueFetchProducts({
          filters: { previouslyOrdered: true },
          limit: 5,
          ...(this.token ? { token: this.token } : {}),
        });

        this.previouslyOrderedProductsData = data;
        this.previouslyOrderedProductsCount = meta.totalCount;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchPreviouslyOrderedProducts() {
      if (!this.isLoggedIn) {
        await this.fetchPreviouslyOrderedProductsPublic();
        return;
      }
      try {
        const {
          data: { data, meta },
        } = await this.venueFetchProducts({
          venueId: this.venueId,
          ...(this.isB2BPlatform || this.isFreemium ? {} : { sortBy: 'name' }),
          filters: { previouslyOrdered: true },
          limit: 5,
        });

        this.previouslyOrderedProductsData = data;
        this.previouslyOrderedProductsCount = meta.totalCount;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchPreviouslyOrdered() {
      this.resetData();
      this.dropdownFilters.previouslyOrdered = true;
      this.refresh({ filters: { previouslyOrdered: true } });
    },
    async fetchTotal() {
      const { data: total } = await this.fetchVenueOrdersHistoryTotal({
        query: {
          scheduledFrom: dayjs().startOf('M').valueOf(),
          scheduledTo: dayjs().endOf('M').valueOf(),
        },
      });
      this.SET_TOTAL_AMOUNT({ totalAmount: total.data });
    },
    openUploadInvoice() {
      this.$refs.uploadInvoice.open();
    },
    ...mapActions('entities/products', [
      'venueFetchProduct',
      'venueFetchProducts',
      'venueFetchProductGroups',
      'publicVenueFetchProductGroups',
      'publicVenueFetchProducts',
      'publicVenueFetchProduct',
    ]),
    ...mapActions('entities/categories', [
      'venueFetchCategoriesNew',
      'publicVenueFetchCategoriesNew',
    ]),
    ...mapActions('entities/venues', ['venueFetchFilterSuppliers', 'fetchB2BLoginContent']),
    ...mapActions('cart', ['fetchTotalProductPrice', 'fetchPublicTotalProductPrice']),
    ...mapActions('entities/orders', ['fetchVenueOrdersHistoryTotal']),
    ...mapActions('tags', ['publicFetchTags', 'venueFetchTags']),
    ...mapMutations('entities/products', ['UPDATE_META']),
    ...mapMutations('entities/orders', ['SET_TOTAL_AMOUNT']),
    ...mapMutations('tags', ['CLEAR_ALL_TAGS', 'SET_SELECTED_TAGS']),
  },
  async created() {
    const viewPreference = localStorage.getItem('viewPreference') || null;
    if (viewPreference) {
      this.isListView = viewPreference !== 'grid';
    }
    if (!this.isLoggedIn) {
      const { token } = this.$route?.query;
      this.token = token;
      try {
        await this.fetchB2BSupplier();
        if (this.b2bSupplier?.public) {
          await Promise.all([
            this.fetchProductTags(),
            this.fetchCategoriesFilter(),
            this.fetchProductGroups(),
            this.refreshPublic(),
            this.fetchPreviouslyOrderedProducts(),
          ]);
          this.SET_SELECTED_TAGS([]);
        } else {
          this.$router.replace({ name: 'platform-login' });
        }
      } catch (e) {
        this.$router.replace({ name: 'platform-login' });
      }
    } else if (this.isB2BPlatform || this.$permission.isFree) {
      await Promise.all([
        this.fetchSuppliersFilter(),
        this.fetchCategoriesFilter(),
        this.fetchProductTags(),
        this.fetchProductGroups(),
        this.refresh(),
        this.fetchPreviouslyOrderedProducts(),
        this.fetchTotal(),
      ]);
      this.SET_SELECTED_TAGS([]);
    } else {
      await Promise.all([
        this.fetchSuppliersFilter(),
        this.fetchCategoriesFilter(),
        this.fetchProductGroups(),
        this.refresh(),
        this.fetchTotal(),
      ]);
    }
    if (this.productId) {
      await this.loadDrawerProduct({ id: this.productId });
    }
  },
  beforeMount() {
    this.$emit('updateFilter', {});
  },
};
</script>

<style scoped lang="scss">
$price-color: #4d7cfe;
$drawer-separator-color: #dee1e4;

$secondary-color: #8790a3;
$primary-color: #252631;

:deep() .loader {
  @extend %whole-screen;
}

.venue-products {
  @include platform-tab-content();

  .ez-load-more {
    margin-top: 2em;
  }
}

:deep() .table tbody tr td.category-cell {
  text-overflow: inherit;
}

:deep() {
  .table {
    .price-cell-overflow,
    .unit-cell {
      overflow: visible;
    }
  }
}

:deep() .select-search {
  color: $color-gray-6C;

  &__list {
    max-height: 244px;
    .category-entity__parent,
    .category-entity__child {
      margin: 3.5px 0px;
      line-height: 1;
    }
  }
}

:deep() .ez-empty-state {
  margin-top: 5rem;
  &__image {
    max-width: none;
  }
  img {
    width: 256px;
    height: 118px;
  }
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  .orders-actions {
    margin-left: auto;
  }

  .view-switch {
    margin-left: auto;
    :deep() .button {
      font-size: 24px;
    }
    :deep() .selected {
      color: $color-primary-blue;
    }
  }
}

.tags-filter {
  :deep() .tags-wrapper {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }

  :deep() .tags-wrapper__selected {
    padding-bottom: 22px;
  }

  :deep() .input-group.search {
    display: flex;
    align-items: center;
    margin-bottom: 22px;
    font-weight: 600;

    @media (max-width: 556px) {
      :deep() .input-group__input {
        font-size: 16px;
      }
    }
  }

  :deep() .input-group__label {
    margin-bottom: 0;
    margin-right: 16px;
  }
}

@media (max-width: 556px) {
  .search {
    :deep() .input-group__input {
      font-size: 16px;
    }
  }
}

:deep() .ez-filter-list .ez-filter-list__items .select-search__trigger {
  width: auto;
  min-width: 128px;
}

:deep() .ez-filter-list .ez-filter-list__items .product-group-filter .select-search__trigger {
  min-width: 172px;
}

:deep() .ez-filter-list .ez-filter-list__items .select-search__wrapper {
  .select-search__dropdown,
  .select-search__dropdown.select-search__dropdown--width-100 {
    min-width: 100%;
    width: max-content;
    color: $color-gray-25;
  }
}

:deep() .ez-filter-list .ez-filter-list__items .ez-select {
  width: auto;
  min-width: 164px;

  .ez-select__dropdown {
    min-width: 100%;
    width: max-content;
  }
  svg {
    color: $color-gray-6C;
  }
}

:deep() .alert {
  margin-top: 0;
}

@media (max-width: 1023px) {
  :deep() .alert .alert__cta {
    flex-shrink: 0;
  }
}

@media (max-width: 768px) {
  .hide-on-mobile {
    display: none;
  }
}
</style>
